
import { LazyTemplate } from "../template";
import { BaseApp, StringFormatArgs } from "../app";
import { EventSource } from "../event-source";
import AppHeader from "../app/header";
import ViewFilters from "./filter/index";
import ViewHelpers from "./helpers";

export abstract class View extends EventSource
{
    public app: BaseApp;
    public template: LazyTemplate;
    public element: HTMLElement;
    
    public iconClass: string;

    public data: any;

    protected _isUnloaded: boolean;
    public get isUnloaded() { return this._isUnloaded; }

    public filters = ViewFilters;
    public helpers = ViewHelpers;

    constructor(data?: any)
    {
        super();
        
        let initialData = this.data !== undefined ? this.data : {};
        data = data !== undefined ? data : {};

        this.data = Object.assign(initialData, data);
        this._isUnloaded = false;
    }

    public beforeRender(): PromiseLike<void>
    {
        this.trigger(ViewEvent.BEFORE_RENDER);
        return Promise.resolve();
    }

    public afterRender(): PromiseLike<void>
    {
        this.trigger(ViewEvent.AFTER_RENDER);
        return Promise.resolve();
    }

    public formSubmitted(form: HTMLFormElement): PromiseLike<void>
    {
        return this.submit(form);
    }
    
    public submit(data?: any): PromiseLike<void>
    {
        this.trigger(ViewEvent.SUBMIT);
        return this.unload();
    }

    public close(): PromiseLike<void>
    {
        this.trigger(ViewEvent.CLOSE);
        if (!this.isUnloaded)
        {
            return this.unload();
        }
        else
        {
            return Promise.resolve();
        }
    }
    
    public unload(): PromiseLike<void>
    {
        this.trigger(ViewEvent.UNLOAD);
        this._isUnloaded = true;
        return Promise.resolve();
    }

    /**
     * Runs a querySelectorAll against the view root element and the view root element itself.
     * @param selector The selector string.
     */
    public querySelector(selector: string): Element[]
    {
        let result: Element[] = [].slice.call(this.element.querySelectorAll(selector));
        if (this.element.matches(selector))
        {
            result.push(this.element);
        }

        return result;
    }

    /**
     * Adds an event listener and attaches it to a selector or an element.
     * @param elementOrSelector Selector or element.
     * @param eventName Name of the event.
     * @param handler Handler.
     */
    public addEventListener(elementOrSelector: Element | Element[] | string, eventName: string | string[], handler: (e: Event) => void): Element[]
    {
        let elements: Element[];
        let eventNames = Array.isArray(eventName) ? eventName : [eventName];

        if (typeof elementOrSelector == 'string')
        {
            elements = [].slice.call(this.element.querySelectorAll(elementOrSelector));
        }

        if (Array.isArray(elementOrSelector))
        {
            elements = elementOrSelector;
        }
        else
        {
            elements = [elementOrSelector as Element];
        }

        for (let element of elements)
        {
            for (let eventName of eventNames)
            {
                element.addEventListener(eventName, handler);
            }
        }
        return elements;
    }

    /**
     * Appends a view element to the view.
     * @param view The view to append.
     */
    public appendChild(view: View)
    {
        this.element.appendChild(view.element);
    }

    public pgettext(context: string, message: string, args?: StringFormatArgs): string
    {
        return this.app.pgettext(context, message, args);
    }

    public ugettext(message: string, args?: StringFormatArgs): string
    {
        return this.app.ugettext(message, args);
    }
}

export interface ViewConstructor
{
    new (data?: any): View;
}


export enum ViewEvent
{
    UNLOAD = 'unload',
    CLOSE = 'close',
    SUBMIT = 'submit',
    BEFORE_RENDER = 'beforeRender',
    AFTER_RENDER = 'afterRender'
}